import React, { useCallback, useEffect, useState } from 'react'
import {
  connectThermalPrinter,
  getThermalPrinterStatus,
  listThermalPrinters,
  printThermalTest,
} from '@renderer/lib/runtime/thermalPrint'
import type { ThermalPrinterInfo } from '@renderer/lib/runtime/types'
import cogoToast from '@successtar/cogo-toast'

interface BahelaThermalSettingsProps {
  open: boolean
  onClose: () => void
}

const BahelaThermalSettings: React.FC<BahelaThermalSettingsProps> = ({ open, onClose }) => {
  const [printers, setPrinters] = useState<ThermalPrinterInfo[]>([])
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<{ connected: boolean; deviceName?: string }>({
    connected: false,
  })

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const [list, st] = await Promise.all([listThermalPrinters(), getThermalPrinterStatus()])
      setPrinters(list)
      setStatus({ connected: st.connected, deviceName: st.deviceName })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) void refresh()
  }, [open, refresh])

  const handleConnect = async (printer: ThermalPrinterInfo): Promise<void> => {
    setLoading(true)
    try {
      const result = await connectThermalPrinter({
        connectionType: printer.connectionType,
        address: printer.address,
        deviceName: printer.name,
      })
      if (result.success) {
        cogoToast.success(`Printer: ${printer.name}`)
        await refresh()
      } else {
        cogoToast.error('Gagal menghubungkan printer')
      }
    } catch {
      cogoToast.error('Gagal menghubungkan printer')
    } finally {
      setLoading(false)
    }
  }

  const handleTest = async (): Promise<void> => {
    setLoading(true)
    try {
      const result = await printThermalTest()
      if (result.success) cogoToast.success('Test print terkirim')
      else cogoToast.error(result.error ?? 'Test print gagal')
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-5 text-gray-900 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Printer Thermal 58mm</h2>
          <button type="button" onClick={onClose} className="text-sm text-gray-500">
            Tutup
          </button>
        </div>
        <p className="mb-3 text-sm text-gray-600">
          {status.connected
            ? `Terhubung: ${status.deviceName ?? 'Printer'}`
            : 'Belum ada printer. Pair Bluetooth di pengaturan Android, lalu pilih di bawah.'}
        </p>
        <div className="mb-3 flex gap-2">
          <button
            type="button"
            disabled={loading}
            onClick={() => void refresh()}
            className="rounded bg-gray-200 px-3 py-2 text-sm"
          >
            Refresh
          </button>
          <button
            type="button"
            disabled={loading || !status.connected}
            onClick={() => void handleTest()}
            className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50"
          >
            Test Print
          </button>
        </div>
        <ul className="space-y-2">
          {printers.length === 0 && !loading && (
            <li className="text-sm text-gray-500">Tidak ada printer ditemukan (BT/USB).</li>
          )}
          {printers.map((p) => (
            <li
              key={`${p.connectionType}-${p.address}`}
              className="flex items-center justify-between rounded border p-2"
            >
              <div>
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-gray-500">
                  {p.connectionType.toUpperCase()} · {p.address}
                </div>
              </div>
              <button
                type="button"
                disabled={loading}
                onClick={() => void handleConnect(p)}
                className="rounded bg-green-600 px-2 py-1 text-xs text-white"
              >
                Pilih
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default BahelaThermalSettings
